/**
 * Client FX — mirrors iOS CurrencyExchangeService (fawazahmed0 CDN + 24h cache).
 */

export interface ExchangeRate {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  lastUpdated: number; // epoch ms
}

const CACHE_KEY = 'wt_fx_rates_v1';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

type RateMap = Record<string, ExchangeRate>;

function loadCache(): RateMap {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ExchangeRate[];
    if (!Array.isArray(parsed)) return {};
    const map: RateMap = {};
    for (const rate of parsed) {
      if (!rate?.fromCurrency || !rate?.toCurrency) continue;
      map[`${rate.fromCurrency}-${rate.toCurrency}`] = rate;
    }
    return map;
  } catch {
    return {};
  }
}

function saveCache(map: RateMap): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(Object.values(map)));
  } catch {
    /* ignore quota */
  }
}

function isFresh(lastUpdated: number): boolean {
  return Date.now() - lastUpdated < CACHE_DURATION_MS;
}

/** Round destination amounts to “nice” numbers — mirrors iOS roundToNiceNumber. */
export function roundToNiceNumber(value: number): number {
  if (value < 1) {
    return Math.round(value * 4) / 4;
  }
  if (value < 10) {
    const rounded = Math.round(value);
    if (rounded <= 2) return rounded;
    if (rounded <= 5) return 5;
    return 10;
  }
  if (value < 100) {
    return Math.round(value / 10) * 10;
  }
  if (value < 1000) {
    return Math.round(value / 50) * 50;
  }
  if (value < 10000) {
    return Math.round(value / 500) * 500;
  }
  return Math.round(value / 1000) * 1000;
}

export async function fetchExchangeRate(
  from: string | null | undefined,
  to: string | null | undefined,
  fetchImpl: typeof fetch = fetch
): Promise<ExchangeRate | null> {
  if (!from || !to || !from.trim() || !to.trim()) return null;
  const fromCode = from.trim().toUpperCase();
  const toCode = to.trim().toUpperCase();
  if (fromCode === toCode) {
    return {
      fromCurrency: fromCode,
      toCurrency: toCode,
      rate: 1,
      lastUpdated: Date.now(),
    };
  }

  const key = `${fromCode}-${toCode}`;
  const cache = loadCache();
  const cached = cache[key];
  if (cached && isFresh(cached.lastUpdated)) {
    return cached;
  }

  const fromLower = fromCode.toLowerCase();
  const url = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${fromLower}.json`;
  try {
    const response = await fetchImpl(url);
    if (!response.ok) {
      return cached ?? null;
    }
    const body = (await response.json()) as Record<string, Record<string, number>>;
    const ratesForBase = body[fromLower];
    const rate = ratesForBase?.[toCode.toLowerCase()];
    if (typeof rate !== 'number' || !Number.isFinite(rate)) {
      return cached ?? null;
    }
    const exchangeRate: ExchangeRate = {
      fromCurrency: fromCode,
      toCurrency: toCode,
      rate,
      lastUpdated: Date.now(),
    };
    cache[key] = exchangeRate;
    saveCache(cache);
    return exchangeRate;
  } catch {
    return cached ?? null;
  }
}

export type FxConversion = { amount: number; converted: number };

/** 12-cell quick reference table (2×6) — mirrors iOS getCommonConversions. */
export function buildCommonConversions(rate: number): FxConversion[] {
  const leftColumnTargets = [1, 2, 5, 10, 50, 100];
  const rightColumnTargets = [200, 400, 800, 1500, 3000, 6000];
  const conversions: FxConversion[] = [];
  let previousValue = 0;

  for (let i = 0; i < 6; i += 1) {
    let leftDestination = roundToNiceNumber(leftColumnTargets[i] * rate);
    if (previousValue === Math.round(leftDestination)) {
      leftDestination += 1;
    }
    previousValue = Math.round(leftDestination);
    conversions.push({
      amount: leftDestination / rate,
      converted: leftDestination,
    });

    const rightDestination = roundToNiceNumber(rightColumnTargets[i] * rate);
    conversions.push({
      amount: rightDestination / rate,
      converted: rightDestination,
    });
  }

  return conversions;
}

export async function getCommonConversions(
  from: string | null | undefined,
  to: string | null | undefined,
  fetchImpl: typeof fetch = fetch
): Promise<FxConversion[] | null> {
  const rate = await fetchExchangeRate(from, to, fetchImpl);
  if (!rate) return null;
  return buildCommonConversions(rate.rate);
}
